import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader } from "@/components/Bits";
import CommandCentreCoach from "@/components/CommandCentreCoach";
import ClientJourney from "@/components/ClientJourney";
import {
  Gauge, WarningCircle, SealCheck, Scales, ShieldCheck, ArrowRight, TrendUp,
  Receipt, CurrencyGbp, Cube, Clock, CheckCircle, Buildings, CalendarCheck,
  Sparkle, EnvelopeSimple, PaperPlaneTilt, UserCircle, BellSlash, XCircle,
  CaretUp, CaretDown, Sun, Compass, Check,
} from "@phosphor-icons/react";

const money0 = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n || 0));

const CATS = [
  { key: "PREPAYMENT", label: "Prepayments", color: "#F37A24", icon: Receipt },
  { key: "ACCRUAL", label: "Accruals", color: "#0E0F3F", icon: TrendUp },
  { key: "DEFERRED_INCOME", label: "Deferred Income", color: "#00A859", icon: CurrencyGbp },
  { key: "DEPRECIATION", label: "Depreciation NBV", color: "#F59E0B", icon: Cube },
];

const ME_STYLES = {
  not_started: { dot: "bg-slate-300", txt: "text-slate-600", chip: "bg-slate-100 text-slate-600", icon: Clock },
  in_progress: { dot: "bg-warn", txt: "text-warn", chip: "bg-warn/15 text-warn", icon: CalendarCheck },
  signed_off: { dot: "bg-positive", txt: "text-positive", chip: "bg-positive/10 text-positive", icon: SealCheck },
};

function Kpi({ label, value, accent = "navy", icon: Icon, sub, testid }) {
  const color = { navy: "text-navy", negative: "text-negative", warn: "text-warn", positive: "text-positive" }[accent] || "text-navy";
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl shadow-card p-5">
      <div className="flex items-center justify-between">
        <div className="overline text-navy/65">{label}</div>
        {Icon && <Icon size={18} className={color} />}
      </div>
      <div className={`font-heading font-black text-3xl tracking-tighter mt-2 tabular ${color}`}>{value}</div>
      {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
    </div>
  );
}

function StackedTrend({ byClient, onFocus, focusTenant }) {
  const labels = byClient?.labels || [];
  const series = byClient?.series || [];
  const months = labels.length || 6;
  const monthTotals = Array.from({ length: months }, (_, i) =>
    series.reduce((s, sr) => s + (sr.values?.[i] || 0), 0));
  const max = Math.max(...monthTotals, 1);
  const H = 168;
  return (
    <div data-testid="cc-trend-chart">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4">
        {series.map((sr) => {
          const clickable = sr.tenant_id && sr.tenant_id !== "__other__";
          const active = focusTenant && sr.tenant_id === focusTenant;
          return (
            <button key={sr.tenant_id} onClick={() => clickable && onFocus?.(sr.tenant_id, sr.name)}
              data-testid={`cc-trend-legend-${sr.tenant_id}`} disabled={!clickable}
              title={clickable ? `Focus on ${sr.name}` : sr.name}
              className={`flex items-center gap-1.5 text-xs rounded-lg px-2 py-1 transition-colors ${clickable ? "hover:bg-slate-100 cursor-pointer" : "cursor-default"} ${active ? "bg-slate-100 text-navy font-semibold" : "text-navy/70"}`}>
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: sr.color }} />
              {sr.name}
            </button>
          );
        })}
      </div>
      <div className="flex items-end gap-3" style={{ height: H + 24 }}>
        {labels.map((lab, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`cc-trend-col-${i}`}>
            <div className="w-full flex flex-col justify-end rounded-lg overflow-hidden" style={{ height: H }}>
              {series.map((sr) => {
                const v = sr.values?.[i] || 0;
                const h = Math.round((v / max) * H);
                if (h < 1) return null;
                return (
                  <motion.div key={sr.tenant_id} initial={{ height: 0 }} animate={{ height: h }} transition={{ duration: 0.5 }}
                    style={{ backgroundColor: sr.color }} title={`${sr.name}: ${money0(v)} (${lab})`} />
                );
              })}
              {monthTotals[i] < 0.01 && <div className="w-full h-[3px] bg-slate-100 rounded" />}
            </div>
            <span className="text-[0.7rem] font-mono text-slate-500">{lab}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BriefingBar() {
  const [pref, setPref] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/command-briefing/preference").then(({ data }) => setPref(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const change = async (val) => {
    setPref((p) => ({ ...p, preference: val }));
    try {
      await api.put("/command-briefing/preference", { preference: val });
      toast.success("Monday briefing preference saved");
      load();
    } catch { toast.error("Could not save preference"); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/command-briefing/send-test");
      if (data.sent) toast.success(`Test briefing sent to ${data.email}`);
      else toast.message(data.reason || "Nothing to send right now");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send test");
    } finally { setSending(false); }
  };

  if (!pref) return null;
  const current = pref.preference === "default" ? pref.effective : pref.preference;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 bg-white border border-slate-200 rounded-2xl shadow-card px-5 py-3.5" data-testid="cc-briefing-bar">
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-orange/10 flex items-center justify-center shrink-0">
          <EnvelopeSimple size={18} className="text-orangeink" />
        </span>
        <div>
          <div className="text-sm font-semibold text-navy">Monday morning briefing</div>
          <div className="text-xs text-slate-600">
            {pref.is_manager ? `You manage ${pref.managed_count} client${pref.managed_count === 1 ? "" : "s"}.` : "Get a weekly needs-attention summary by email."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:ml-auto">
        <select value={current} onChange={(e) => change(e.target.value)} data-testid="cc-briefing-select"
          className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none">
          <option value="all">All clients</option>
          <option value="managed">My clients only</option>
          <option value="off">Off</option>
        </select>
        <button onClick={sendTest} disabled={sending} data-testid="cc-briefing-test"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors disabled:opacity-60">
          <PaperPlaneTilt size={15} /> {sending ? "Sending…" : "Send test"}
        </button>
      </div>
    </div>
  );
}

export default function CommandCentre() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [scope, setScope] = useState("all");
  const [focus, setFocus] = useState(null);
  const [coachReplay, setCoachReplay] = useState(0);

  const load = useCallback(() => {
    const params = [];
    if (scope === "mine") params.push("scope=mine");
    if (focus) params.push(`tenant=${encodeURIComponent(focus.tenant_id)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    api.get(`/command-centre${qs}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ count: 0, entities: [], worklist: [] }));
  }, [scope, focus]);

  useEffect(() => { load(); }, [load]);

  const changeScope = (sc) => { setFocus(null); setScope(sc); };

  const sigFor = (tid) => {
    const e = (data?.entities || []).find((x) => x.tenant_id === tid)
      || (data?.worklist || []).find((x) => x.tenant_id === tid);
    return e?.alert_signature || "";
  };

  const markReviewed = async (tid) => {
    const sig = sigFor(tid);
    // Clear optimistically first so the badge disappears instantly on click.
    setData((d) => {
      if (!d) return d;
      const upd = (arr) => (arr || []).map((e) => (e.tenant_id === tid ? { ...e, alerts_new: false } : e));
      const entities = upd(d.entities);
      return { ...d, entities, worklist: upd(d.worklist),
        totals: { ...d.totals, unreviewed: entities.filter((e) => e.alerts_new).length } };
    });
    window.dispatchEvent(new Event("badges:refresh"));
    try { await api.post("/command-centre/mark-reviewed", { tenant_id: tid, signature: sig }); } catch { /* non-blocking */ }
  };

  const view = async (tid, dest = "/dashboard") => {
    try {
      await api.post("/command-centre/mark-reviewed", { tenant_id: tid, signature: sigFor(tid) }).catch(() => {});
      await api.post("/xero/organisations/activate", { tenant_id: tid });
      localStorage.setItem("lottie_workspace", "org");
      localStorage.setItem("lottie_collapse_nav", "1");
      window.location.assign(dest);
    } catch {
      toast.error("Could not switch entity. Please try again.");
    }
  };

  const dismissMissing = async (tid) => {
    try {
      await api.post("/command-centre/snooze-missing", { tenant_id: tid });
      toast.success("Missing-accrual flag dismissed for this month");
      load();
      window.dispatchEvent(new Event("badges:refresh"));
    } catch {
      toast.error("Could not dismiss the flag");
    }
  };

  if (data === null) {
    return <Page><span className="font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</span></Page>;
  }

  const n = data.count || 0;
  const t = data.totals || {};
  const me = data.month_end || {};
  const exposure = t.exposure_by_type || {};
  const maxExp = Math.max(...CATS.map((c) => Math.abs(exposure[c.key] || 0)), 1);
  const meTotal = (me.not_started || 0) + (me.in_progress || 0) + (me.signed_off || 0);

  // Trend % change vs the first month that had exposure.
  const trend = data.exposure_trend || [];
  const lastVal = trend.length ? trend[trend.length - 1].value : 0;
  const baseIdx = trend.findIndex((x) => Math.abs(x.value) > 0.01);
  let trendBadge = null;
  if (baseIdx >= 0 && baseIdx < trend.length - 1) {
    const base = trend[baseIdx].value;
    const chg = ((lastVal - base) / Math.abs(base)) * 100;
    trendBadge = { pct: Math.abs(Math.round(chg)), up: chg >= 0, label: trend[baseIdx].label, flat: Math.round(chg) === 0 };
  } else if (lastVal > 0.01) {
    trendBadge = { newp: true };
  }

  // Morning hero.
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").trim().split(" ")[0];
  const na = t.needs_attention || 0;
  const heroLine = na === 0
    ? "Everything's on track — nothing needs you today."
    : `${na} client${na === 1 ? "" : "s"} need${na === 1 ? "s" : ""} you today · ${money0(t.due_this_month)} of journals due this month${t.journals_overdue ? ` · ${t.journals_overdue} overdue` : ""}.`;

  const ScopeToggle = (
    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl" data-testid="cc-scope-toggle">
      <button onClick={() => changeScope("all")} data-testid="cc-scope-all"
        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${scope === "all" ? "bg-white shadow-card text-navy" : "text-slate-600 hover:text-navy"}`}>
        All clients
      </button>
      <button onClick={() => changeScope("mine")} data-testid="cc-scope-mine"
        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${scope === "mine" ? "bg-white shadow-card text-navy" : "text-slate-600 hover:text-navy"}`}>
        My clients
      </button>
    </div>
  );

  return (
    <Page>
      <PageHeader overline="Practice" title="Command Centre"
        subtitle={`A live roll-up across ${scope === "mine" ? "the clients you manage" : `all ${n} connected ${n === 1 ? "client" : "clients"}`}.`}>
        <div className="flex items-center gap-2">
          {n > 0 && (
            <button onClick={() => setCoachReplay((c) => c + 1)} data-testid="cc-show-around"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-medium border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
              <Compass size={15} /> Show me around
            </button>
          )}
          {ScopeToggle}
        </div>
      </PageHeader>

      {n === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="cc-empty">
          <Gauge size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 text-sm">
            {scope === "mine" ? "You're not the account manager for any clients yet." : "No Xero organisations connected yet."}
          </p>
          {scope === "mine" && (
            <button onClick={() => setScope("all")} data-testid="cc-empty-viewall"
              className="mt-4 inline-flex items-center gap-1.5 text-orangeink text-sm font-semibold hover:gap-2.5 transition-all">
              View all clients <ArrowRight size={14} />
            </button>
          )}
        </div>
      ) : (
        <>
          <CommandCentreCoach enabled={scope === "all" && !focus} replay={coachReplay} />
          <BriefingBar />

          {focus && (
            <div className="flex items-center gap-2 mb-6 bg-navy/[0.04] border border-slate-200 rounded-xl px-4 py-2.5 w-fit" data-testid="cc-focus-banner">
              <Buildings size={15} className="text-navy/50" />
              <span className="text-sm text-navy">Focused on <span className="font-semibold">{focus.name}</span></span>
              <button onClick={() => setFocus(null)} data-testid="cc-focus-clear"
                className="ml-1 inline-flex items-center gap-1 text-xs font-medium text-orangeink hover:text-navy transition-colors">
                Clear <XCircle size={14} weight="fill" />
              </button>
            </div>
          )}

          {/* Morning hero */}
          <div className="flex items-start gap-3 mb-6 rounded-2xl px-5 py-4 bg-navy text-white shadow-card" data-testid="cc-hero">
            <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              {na === 0 ? <CheckCircle size={20} className="text-positive" weight="fill" /> : <Sun size={20} className="text-orange" weight="fill" />}
            </span>
            <div>
              <div className="font-heading font-bold text-lg leading-tight">
                {greet}{firstName ? `, ${firstName}` : ""}
              </div>
              <div className="text-sm text-white/80 mt-0.5" data-testid="cc-hero-line">{heroLine}</div>
            </div>
          </div>

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mb-6" data-testid="cc-kpis">
            <Kpi label="Total exposure" value={money0(t.total_exposure)} icon={Gauge} testid="cc-kpi-exposure"
              sub={`${t.active_schedules || 0} active schedules`} />
            <Kpi label="Needs attention" value={t.needs_attention || 0} accent={t.needs_attention ? "negative" : "positive"}
              icon={t.needs_attention ? WarningCircle : CheckCircle} testid="cc-kpi-attention"
              sub={t.needs_attention ? `${t.needs_attention} of ${n} clients` : "All clients on track"} />
            <Kpi label="Overdue journals" value={t.journals_overdue || 0} accent={t.journals_overdue ? "negative" : "navy"}
              icon={Clock} testid="cc-kpi-overdue" sub={`${t.journals_due || 0} due this month`} />
            <Kpi label="Missing accruals" value={t.missing_accruals || 0} accent={t.missing_accruals ? "warn" : "positive"}
              icon={Sparkle} testid="cc-kpi-missing" sub={t.missing_accruals ? "recurring suppliers not billed" : "none detected"} />
            <Kpi label="To sign off" value={me.still_to_sign_off || 0} accent={me.still_to_sign_off ? "warn" : "positive"}
              icon={SealCheck} testid="cc-kpi-signoff" sub={`${me.signed_off || 0} signed off`} />
          </div>

          {/* Exposure trend (stacked by client) */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 mb-6" data-testid="cc-trend">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="font-heading font-bold text-lg text-navy tracking-tight">Portfolio exposure — last 6 months</h3>
                {trendBadge && !trendBadge.newp && (
                  <span data-testid="cc-trend-badge"
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${trendBadge.flat ? "bg-slate-100 text-slate-600" : trendBadge.up ? "bg-warn/15 text-warn" : "bg-positive/10 text-positive"}`}>
                    {trendBadge.flat ? "flat" : (<>{trendBadge.up ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}{trendBadge.pct}%</>)} since {trendBadge.label}
                  </span>
                )}
                {trendBadge?.newp && (
                  <span data-testid="cc-trend-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange/10 text-orangeink">
                    <Sparkle size={12} weight="fill" /> new this period
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-600">remaining balance at each month-end, by client</span>
            </div>
            <StackedTrend byClient={data.exposure_trend_by_client} focusTenant={focus?.tenant_id}
              onFocus={(tid, name) => setFocus({ tenant_id: tid, name })} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Exposure by type */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6" data-testid="cc-exposure">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-bold text-lg text-navy tracking-tight">Exposure by type</h3>
                <span className="text-xs text-slate-600">across the portfolio</span>
              </div>
              <div className="space-y-4">
                {CATS.map((c) => {
                  const v = exposure[c.key] || 0;
                  const pct = Math.round((Math.abs(v) / maxExp) * 100);
                  return (
                    <div key={c.key} data-testid={`cc-exposure-${c.key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-2 text-sm text-navy">
                          <c.icon size={15} style={{ color: c.color }} /> {c.label}
                        </span>
                        <span className="font-heading font-bold text-navy tabular">{money0(v)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }}
                          className="h-full rounded-full" style={{ backgroundColor: c.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-600">Approvals waiting · {t.approvals_waiting || 0}</span>
                <span className="text-sm text-slate-600">Unresolved variances · {t.variances || 0}</span>
              </div>
            </div>

            {/* Month-end status */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-card p-6" data-testid="cc-monthend">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-bold text-lg text-navy tracking-tight">Month-end status</h3>
                <span className="text-xs text-slate-600">{meTotal} {meTotal === 1 ? "client" : "clients"}</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-5" data-testid="cc-monthend-bar">
                {["signed_off", "in_progress", "not_started"].map((k) => {
                  const val = me[k] || 0;
                  if (!val) return null;
                  const w = (val / (meTotal || 1)) * 100;
                  const bg = { signed_off: "#00A859", in_progress: "#F59E0B", not_started: "#CBD5E1" }[k];
                  return <div key={k} style={{ width: `${w}%`, backgroundColor: bg }} title={`${val}`} />;
                })}
              </div>
              <div className="space-y-3">
                {[
                  { k: "signed_off", label: "Signed off" },
                  { k: "in_progress", label: "In progress" },
                  { k: "not_started", label: "Not started" },
                ].map(({ k, label }) => {
                  const s = ME_STYLES[k];
                  return (
                    <div key={k} className="flex items-center gap-2.5" data-testid={`cc-monthend-${k}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                      <span className="text-sm text-navy flex-1">{label}</span>
                      <span className={`font-heading font-bold tabular ${s.txt}`}>{me[k] || 0}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-600">
                {me.still_to_sign_off ? (
                  <span><span className="font-semibold text-warn">{me.still_to_sign_off}</span> client{me.still_to_sign_off === 1 ? "" : "s"} still to sign off this month-end.</span>
                ) : (
                  <span className="text-positive font-medium">Every client's month-end is signed off. 🎉</span>
                )}
              </div>
            </div>
          </div>

          {/* Month-end progress by client */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden mb-6" data-testid="cc-client-journey">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2.5">
              <Compass size={18} className="text-orangeink" />
              <h3 className="font-heading font-bold text-lg text-navy tracking-tight">Month-end progress by client</h3>
              <span className="ml-auto text-xs text-slate-600 hidden sm:inline">Review → Post → Reconcile → Approve → Sign-off</span>
            </div>
            <div className="divide-y divide-slate-100">
              {[...(data.entities || [])]
                .sort((a, b) => {
                  const aDone = a.month_end?.state === "signed_off" ? 1 : 0;
                  const bDone = b.month_end?.state === "signed_off" ? 1 : 0;
                  if (aDone !== bDone) return aDone - bDone;
                  return (b.urgency || 0) - (a.urgency || 0);
                })
                .map((e) => (
                  <div key={e.tenant_id} data-testid={`cc-journey-row-${e.tenant_id}`}
                    className="px-6 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60 transition-colors">
                    <div className="flex items-center gap-2 min-w-0 sm:w-52 shrink-0">
                      <Buildings size={15} className="text-navy/40 shrink-0" />
                      <button onClick={() => setFocus({ tenant_id: e.tenant_id, name: e.tenant_name })}
                        title={`Focus on ${e.tenant_name}`}
                        className="font-semibold text-navy text-sm truncate hover:text-orangeink transition-colors text-left">
                        {e.tenant_name}
                      </button>
                      {e.month_end?.state === "signed_off" && (
                        <span className="shrink-0 text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-positive/10 text-positive">Done</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 overflow-x-auto">
                      <ClientJourney entity={e} onStep={(s) => view(e.tenant_id, s.to)} />
                    </div>
                    <button onClick={() => view(e.tenant_id)} data-testid={`cc-journey-open-${e.tenant_id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-orangeink text-sm font-semibold hover:gap-2.5 transition-all">
                      Open <ArrowRight size={14} />
                    </button>
                  </div>
                ))}
            </div>
          </div>

          {/* Needs-attention worklist */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden" data-testid="cc-worklist">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2.5">
              <WarningCircle size={18} className="text-negative" />
              <h3 className="font-heading font-bold text-lg text-navy tracking-tight">Needs attention</h3>
              <span className="ml-auto text-xs text-slate-600">sorted by urgency</span>
            </div>
            {(data.worklist || []).length === 0 ? (
              <div className="px-6 py-12 text-center" data-testid="cc-worklist-clear">
                <CheckCircle size={36} className="mx-auto text-positive mb-3" weight="fill" />
                <p className="text-sm text-navy font-medium">Nothing needs attention right now.</p>
                <p className="text-xs text-slate-600 mt-1">No overdue journals, unresolved variances, missing accruals or unsigned month-ends across your clients.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.worklist.map((e, i) => {
                  const meS = ME_STYLES[e.month_end?.state] || ME_STYLES.not_started;
                  const mgr = e.account_manager?.name;
                  return (
                    <motion.div key={e.tenant_id} data-testid={`cc-worklist-${e.tenant_id}`}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: 0.04 * i }}
                      className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60 transition-colors">
                      <div className="flex flex-col gap-1 min-w-0 sm:w-56 shrink-0">
                        <div className="flex items-center gap-2.5">
                          <Buildings size={16} className="text-navy/40 shrink-0" />
                          <button onClick={() => setFocus({ tenant_id: e.tenant_id, name: e.tenant_name })}
                            data-testid={`cc-worklist-focus-${e.tenant_id}`} title={`Focus on ${e.tenant_name}`}
                            className="font-semibold text-navy truncate hover:text-orangeink transition-colors text-left">
                            {e.tenant_name}
                          </button>
                          {e.demo && <span className="text-[0.55rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#F37A24]/15 text-[#C2410C]">Demo</span>}
                          {e.alerts_new && (
                            <span data-testid={`cc-new-${e.tenant_id}`} title="New or changed alerts since you last reviewed this org"
                              className="text-[0.55rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-negative text-white animate-pulse">
                              New
                            </span>
                          )}
                        </div>
                        {mgr && (
                          <span className="flex items-center gap-1 text-[0.7rem] text-slate-500 pl-6" data-testid={`cc-manager-${e.tenant_id}`}>
                            <UserCircle size={12} /> {mgr}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 flex-1">
                        {e.journals_overdue > 0 && (
                          <button onClick={() => view(e.tenant_id, "/close")} data-testid={`cc-chip-overdue-${e.tenant_id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-negative/10 text-negative hover:bg-negative hover:text-white transition-colors">
                            <Clock size={12} /> {e.journals_overdue} overdue
                          </button>
                        )}
                        {e.variances > 0 && (
                          <button onClick={() => view(e.tenant_id, "/reconciliation")} data-testid={`cc-chip-variance-${e.tenant_id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-warn/15 text-warn hover:bg-warn hover:text-white transition-colors">
                            <Scales size={12} /> {e.variances} variance{e.variances === 1 ? "" : "s"}
                          </button>
                        )}
                        {e.missing_accruals > 0 && (
                          <span className="inline-flex items-center rounded-full overflow-hidden">
                            <button onClick={() => view(e.tenant_id, "/recurring-accruals")} data-testid={`cc-chip-missing-${e.tenant_id}`}
                              className="inline-flex items-center gap-1.5 pl-2.5 pr-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white transition-colors">
                              <Sparkle size={12} /> {e.missing_accruals} missing accrual{e.missing_accruals === 1 ? "" : "s"}
                            </button>
                            <button onClick={() => dismissMissing(e.tenant_id)} data-testid={`cc-dismiss-missing-${e.tenant_id}`}
                              title="Dismiss — not needed this month"
                              className="inline-flex items-center px-1.5 py-1 bg-indigo-50 text-indigo-400 hover:bg-slate-200 hover:text-slate-700 transition-colors">
                              <BellSlash size={12} />
                            </button>
                          </span>
                        )}
                        {e.approvals_waiting > 0 && (
                          <button onClick={() => view(e.tenant_id, "/approvals")} data-testid={`cc-chip-approve-${e.tenant_id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-orange/10 text-orangeink hover:bg-orange hover:text-white transition-colors">
                            <ShieldCheck size={12} /> {e.approvals_waiting} to approve
                          </button>
                        )}
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meS.chip}`}>
                          <meS.icon size={12} /> {e.month_end?.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-sm text-navy/70 tabular hidden sm:inline">{money0(e.exposure)}</span>
                        {e.alerts_new && (
                          <button onClick={() => markReviewed(e.tenant_id)} data-testid={`cc-review-${e.tenant_id}`}
                            title="Mark this org's alerts as reviewed"
                            className="inline-flex items-center gap-1 text-navy/55 text-xs font-medium hover:text-navy transition-colors">
                            <Check size={13} /> Mark reviewed
                          </button>
                        )}
                        <button onClick={() => view(e.tenant_id)} data-testid={`cc-view-${e.tenant_id}`}
                          className="inline-flex items-center gap-1.5 text-orangeink text-sm font-semibold hover:gap-2.5 transition-all">
                          Open <ArrowRight size={14} />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Page>
  );
}
